/**
 * Formats seconds into M:SS format
 * @param {number} seconds - The number of seconds to format
 * @returns {string} The formatted time string in M:SS format
 */
export const formatTimeToMMSS = seconds => {
  if (!seconds && seconds !== 0) return "";

  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = Math.floor(seconds % 60);

  return `${minutes.toString()}:${remainingSeconds.toString().padStart(2, "0")}`;
};

/**
 * Parses M:SS format into seconds
 * @param {string} timeString - The time string in M:SS format
 * @returns {number} The number of seconds
 */
export const parseMMSSToSeconds = timeString => {
  if (!timeString) return 0;

  const [minutes, seconds] = timeString.split(":").map(Number);
  if (isNaN(minutes) || isNaN(seconds)) return 0;

  return minutes * 60 + seconds;
};

/**
 * Humanize a duration in seconds — used by "Total runtime" cards
 * on the audiobook + similar pages. Two stylistic variants for
 * caller preference:
 *   • `units: 'short'` (default) → "2h 30m" / "45m"
 *   • `units: 'long'`            → "2 hr 30 min" / "45 min"
 *
 * `empty` controls the return value when the input is missing /
 * zero / negative — some call sites want a visible placeholder
 * ('—'), others want a falsy value so they can hide the field
 * entirely (`null`).
 *
 *   humanizeDuration(150)                       // "2m"
 *   humanizeDuration(3661, { units: 'long' })   // "1 hr 1 min"
 *   humanizeDuration(0, { empty: null })        // null
 *
 * @param {number|null|undefined} totalSec
 * @param {{ units?: 'short' | 'long', empty?: string | null }} [options]
 * @returns {string|null}
 */
export function humanizeDuration(totalSec, options = {}) {
  const { units = 'short', empty = '—' } = options;
  if (!totalSec || totalSec <= 0) return empty;
  const minutes = Math.floor(totalSec / 60);
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (units === 'long') {
    if (h > 0 && m > 0) return `${h} hr ${m} min`;
    if (h > 0)          return `${h} hr`;
    return `${m} min`;
  }
  if (h > 0 && m > 0)   return `${h}h ${m}m`;
  if (h > 0)            return `${h}h`;
  return `${m}m`;
}
